import React from 'react';
import { Formik, Form } from 'formik';
import {
  CircularProgress,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { PLRFormProps } from '../../../../../../types/preloan';
import { FormSchemaV3 } from '../../../../../../types/preloan/v3';
import DataForm from '../../../../DataForm';
import { patchFillForm, postPreApprove } from '../../../../../../queries/preloan/v3';
import { getInterestRate } from '../../../../../../queries/preloan';
import ContactForm from '../../../../ContactForm';
import {
  archiveUnemployedPLR,
  partnerErrorRedirectURL, preLoanRequestStep,
} from '../../formLogic';
import analytics, { addPreApproveId } from '../../../../../../utils/analytics';
import { inviteePlrCategory } from '../../../ComplementInvite/analyticsPages';
import { fieldsFromPreApprove } from '../02_dataFetching/components/helpers';

export default function FillForm<T extends FormSchemaV3>(props: PLRFormProps<T>) {
  const {
    formRef,
    formSchema,
    handleEnableLoading,
    handleCurrentData,
    handleStep,
  } = props;
  const [contactFormOk, setContactFormOk] = React.useState(false);
  const [dataFormOk, setDataFormOk] = React.useState(false);
  const [contactFormData, setContactFormData] = React.useState(formSchema.contactForm);
  const [dataFormData, setDataFormData] = React.useState(formSchema.dataForm);
  const [error, setError] = React.useState(false);
  const [errorExternalInformation, setErrorExternalInformation] = React.useState(false);
  const handleSubmitDataForm = (values: FormSchemaV3['dataForm']) => {
    setDataFormOk(true);
    setDataFormData(values);
  };
  const handleCloseDataForm = () => setDataFormOk(false);
  const handleSubmitContactForm = (values: FormSchemaV3['contactForm']) => {
    setContactFormOk(true);
    setContactFormData(values);
  };
  const handleCloseContactForm = () => setContactFormOk(false);

  React.useEffect(() => {
    if (error) {
      const redirectURL = partnerErrorRedirectURL(
        formSchema.origin,
        formSchema.rut,
        formSchema.config,
      );
      if (redirectURL) {
        setTimeout(() => { window.location.href = redirectURL; }, 1000);
      }
    }
  }, [error]);

  const hasContactFormData = Object.keys(formSchema.contactForm).length > 0;
  const hasDataFormData = Object.keys(formSchema.dataForm).length > 0;

  const [triggerOpenContactForm, setTriggerOpenContactForm] = React.useState(false);
  const [triggerOpenDataForm, setTriggerOpenDataForm] = React.useState(false);
  const autoSubmitted = React.useRef(false);

  React.useEffect(() => {
    if (hasContactFormData && hasDataFormData) {
      if (contactFormOk && dataFormOk) {
        return handleEnableLoading(false);
      } if (contactFormOk) {
        analytics.page(
          inviteePlrCategory(formSchema.complementToken, 'PLR - CONFIRMAR DATOS'),
          'DATOS COMPLEMENTARIOS',
        );
        setTimeout(() => setTriggerOpenDataForm(true), 500);
      } else if (dataFormOk) {
        analytics.page(
          inviteePlrCategory(formSchema.complementToken, 'PLR - CONFIRMAR DATOS'),
          'DATOS DE CONTACTO',
        );
        setTimeout(() => setTriggerOpenContactForm(true), 500);
      }
    } else if (hasContactFormData) {
      if (contactFormOk) {
        if (!autoSubmitted.current) {
          autoSubmitted.current = true;
          formRef.current?.handleSubmit();
        }
        return undefined;
      }
    } else if (hasDataFormData) {
      if (dataFormOk) {
        return handleEnableLoading(false);
      }
    }
    return handleEnableLoading(true);
  }, [contactFormOk, dataFormOk]);

  React.useEffect(() => {
    const category = inviteePlrCategory(
      formSchema.complementToken,
      formSchema.isRedirect ? `PLR - ${formSchema.isRedirect}` : 'PLR - CONFIRMAR DATOS',
    );
    analytics.page(category, 'CONFIRMAR DATOS');
    handleCurrentData({ ...formSchema, isRedirect: false });
    setTimeout(() => {
      if (hasContactFormData) {
        analytics.page(
          inviteePlrCategory(formSchema.complementToken, 'PLR - CONFIRMAR DATOS'),
          'DATOS DE CONTACTO',
        );
        setTriggerOpenContactForm(true);
      } else if (hasDataFormData) {
        analytics.page(
          inviteePlrCategory(formSchema.complementToken, 'PLR - CONFIRMAR DATOS'),
          'DATOS COMPLEMENTARIOS',
        );
        setTriggerOpenDataForm(true);
      }
    }, 500);
  }, []);

  return (
    <Formik
      innerRef={formRef}
      initialValues={formSchema}
      onSubmit={async (values) => {
        try {
          handleEnableLoading(true);
          const parsedQueryData = {} as { [key: string]: string | number };
          Object.keys(contactFormData).forEach((key) => {
            parsedQueryData[key] = contactFormData[key]?.value.trim();
          });
          Object.keys(dataFormData).forEach((key) => {
            parsedQueryData[key] = dataFormData[key]?.type === 'number'
              ? +(dataFormData[key].value.trim())
              : dataFormData[key]?.value.trim();
          });
          const { data } = await patchFillForm(values.PLRtoken, parsedQueryData);
          if (dataFormData.type_of_contract?.value === 'DESEMPLEADO') {
            return archiveUnemployedPLR(formSchema, handleCurrentData, handleStep);
          }

          switch (data.plr.step) {
            case 'DATA_OK': {
              const { data: preApproveData } = await postPreApprove(formSchema.PLRtoken);
              if (preApproveData.preApproves?.length > 0) {
                const mainPreApprove = preApproveData.preApproves.find((pa) => pa.productSubtype === 'main');
                const selectedPreApprove = mainPreApprove || preApproveData.preApproves[0];
                addPreApproveId(selectedPreApprove.id);
              }
              switch (preApproveData.plr.step) {
                case 'LOAN_SIMULATION': {
                  const interestRate = await getInterestRate(formSchema.PLRtoken);
                  handleCurrentData({
                    ...formSchema,
                    step: preApproveData.plr.step,
                    loanParams: interestRate.data,
                  });
                  return handleStep(preLoanRequestStep.LOAN_SIMULATION);
                }
                case 'MANUAL_VERIFICATION':
                case 'READY_FOR_LOAN': {
                  handleCurrentData({
                    ...formSchema,
                    step: preApproveData.plr.step,
                    preApproves: preApproveData.preApproves,
                    ...fieldsFromPreApprove(preApproveData),
                  });
                  return handleStep(preLoanRequestStep.READY_FOR_LOAN);
                }
                case 'ARCHIVED_FAIL': {
                  handleCurrentData({
                    ...formSchema,
                    step: preApproveData.plr.step,
                    firstName: preApproveData.plr.firstName,
                    lastName: preApproveData.plr.lastName,
                    rut: preApproveData.plr.idNumber,
                    results: preApproveData.results,
                    ...fieldsFromPreApprove(preApproveData),
                    origin: preApproveData.plr.origin,
                    adminComment: preApproveData.plr.adminComment,
                  });
                  return handleStep(preLoanRequestStep.ARCHIVED_FAIL);
                }
                default:
                  return setError(true);
              }
            }
            case 'ARCHIVED_FAIL': {
              handleCurrentData({
                ...formSchema,
                step: data.plr.step,
                firstName: data.plr.firstName,
                lastName: data.plr.lastName,
                rut: data.plr.idNumber,
                results: data.results,
                origin: data.plr.origin,
                adminComment: data.plr.adminComment,
              });
              return handleStep(preLoanRequestStep.ARCHIVED_FAIL);
            }
            case 'MANUAL_VERIFICATION':
            case 'READY_FOR_LOAN': {
              handleCurrentData({
                ...formSchema,
                step: data.plr.step,
              });
              return handleStep(preLoanRequestStep.READY_FOR_LOAN);
            }
            default:
              return setError(true);
          }
        } catch (e) {
          if (e === 509) {
            analytics.track('ERROR DE FUENTE INFORMACION EXTERNA', {
              initiator: 'backend',
              errorCode: e,
            });
            setErrorExternalInformation(true);
          } else {
            analytics.track('ERROR', {
              initiator: 'backend',
              errorCode: e,
            });
          }
          return setError(true);
        }
      }}
    >
      {({
        values,
        isSubmitting,
      }) => (

        <Form>
          <Typography variant="h6" align="center" gutterBottom>
            Confirmar datos
          </Typography>
          <br />
          <Typography>
            ¡Aún nos falta información para tu evaluación!
            {' '}
            <b>
              Por favor confirma los datos solicitados a continuación
            </b>
            {' '}
            para poder evaluarte oportunamente
          </Typography>
          <br />
          {hasContactFormData
            && (
              <div style={{
                display: 'flex',
                placeContent: 'center',
                alignItems: 'center',
                paddingLeft: 40,
              }}
              >
                <ContactForm
                  onSubmit={handleSubmitContactForm}
                  handleCloseContactForm={handleCloseContactForm}
                  formSchema={values}
                  triggerOpen={triggerOpenContactForm}
                />
                <div style={{ width: 50, paddingTop: 5 }}>
                  {contactFormOk && <CheckCircleOutlineIcon style={{ color: 'green' }} />}
                </div>
              </div>
            )}
          <br />
          {hasDataFormData
            && (
              <div style={{
                display: 'flex',
                placeContent: 'center',
                alignItems: 'center',
                paddingLeft: 40,
              }}
              >
                <DataForm
                  onSubmit={handleSubmitDataForm}
                  handleCloseDataForm={handleCloseDataForm}
                  formSchema={values}
                  triggerOpen={triggerOpenDataForm}
                />
                <div style={{ width: 50, paddingTop: 5 }}>
                  {dataFormOk && <CheckCircleOutlineIcon style={{ color: 'green' }} />}
                </div>
              </div>
            )}
          <br />
          {error
            && (
              <Typography
                variant="caption"
                color="error"
              >
                {errorExternalInformation
                  ? 'No hemos podido procesar tu evaluación debido a que uno de nuestros proveedores de información se encuentra temporalmente fuera de servicio. Por favor vuelve más tarde para que podamos evaluarte oportunamente.'
                  : 'Ha ocurrido un error. Por favor contáctate con nosotros.'}
              </Typography>
            )}
          <br />
          {isSubmitting
            && (
              <Typography>
                Cargando...
                {' '}
                <CircularProgress size={10} />
              </Typography>
            )}
        </Form>
      )}

    </Formik>
  );
}
